import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckoutForm } from './CheckoutForm';
import { OrderSummary } from './OrderSummary';
import CurrencyToggle from '../CurrencyToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { bundleId, modality: initialModality } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const { currency, fmt } = useCurrency();
  
  const [bundle, setBundle] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: dados, 2: turma, 3: pagamento
  const [selectedModality, setSelectedModality] = useState(initialModality);
  const [paypalConfig, setPaypalConfig] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    whatsapp: '',
    document: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    schedule_id: '',
    payment_method: 'credit_card',
    installments: 1
  });

  const fetchData = useCallback(async () => {
    try {
      const bundleRes = await axios.get(`${API}/bundles/${bundleId}`);
      setBundle(bundleRes.data);
      
      // Apenas buscar schedules se for Elite ou Mentor
      if (selectedModality === 'ead_live' || selectedModality === 'presential') {
        const schedulesRes = await axios.get(`${API}/schedules?bundle_id=${bundleId}&modality=${selectedModality}`);
        setSchedules(schedulesRes.data);
      } else {
        // Para EaD gravado, não há turmas específicas
        setSchedules([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [bundleId, selectedModality, navigate]);

  useEffect(() => {
    // Wait for auth context to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchData();
  }, [authLoading, isAuthenticated, navigate, fetchData]);

  useEffect(() => {
    axios.get(`${API}/paypal/config`)
      .then((res) => setPaypalConfig(res.data))
      .catch(() => setPaypalConfig(null));
  }, []);

  useEffect(() => {
    if (currency !== 'BRL' && formData.payment_method !== 'paypal') {
      setFormData((f) => ({ ...f, payment_method: 'paypal' }));
    }
  }, [currency, formData.payment_method]);

  // Handle modality change
  const handleModalityChange = (newModality) => {
    setSelectedModality(newModality);
    setFormData({ ...formData, schedule_id: '' }); // Reset schedule when modality changes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Só exigir turma para Elite e Mentor
    if ((selectedModality === 'ead_live' || selectedModality === 'presential') && !formData.schedule_id) {
      toast.error('Selecione uma turma');
      return;
    }

    setProcessing(true);

    try {
      const response = await axios.post(
        `${API}/checkout`,
        {
          ...formData,
          user_id: user.id,
          bundle_id: parseInt(bundleId),
          schedule_id: formData.schedule_id ? parseInt(formData.schedule_id) : null,
          modality: selectedModality
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Matrícula realizada com sucesso!');
      
      // Mostrar confirmação
      navigate(`/checkout/success/${response.data.enrollment_id}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar matrícula');
    } finally {
      setProcessing(false);
    }
  };

  const modalityLabels = {
    ead_recorded: 'EaD Gravado (On-demand)',
    ead_live: 'Elite (Ao Vivo)',
    presential: 'Mentor (Presencial)'
  };

  const buildCheckoutPayload = () => ({
    full_name: formData.full_name,
    email: formData.email,
    phone: formData.phone,
    whatsapp: formData.whatsapp,
    document: formData.document,
    address: formData.address,
    city: formData.city,
    state: formData.state,
    postal_code: formData.postal_code,
    payment_method: 'paypal',
    installments: 1,
    bundle_id: parseInt(bundleId),
    schedule_id: formData.schedule_id ? parseInt(formData.schedule_id) : null,
    modality: selectedModality
  });

  const validatePaypalForm = () => {
    const required = ['full_name', 'email', 'phone', 'whatsapp', 'document', 'address', 'city', 'state', 'postal_code'];
    for (const field of required) {
      if (!formData[field] || !String(formData[field]).trim()) {
        toast.error('Preencha todos os dados pessoais e de endereço antes de pagar.');
        return false;
      }
    }
    return true;
  };

  const handlePaypalCreateOrder = async () => {
    if (!validatePaypalForm()) {
      throw new Error('Formulário incompleto');
    }
    const res = await axios.post(`${API}/paypal/create-order`, {
      bundle_id: parseInt(bundleId),
      modality: selectedModality,
      currency: currency
    });
    return res.data.id;
  };

  const handlePaypalApprove = async (data) => {
    try {
      const res = await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        checkout: buildCheckoutPayload()
      });
      if (res.data.success) {
        toast.success('Pagamento aprovado! Matrícula confirmada.');
        navigate(`/checkout/success/${res.data.enrollment_id}`);
      } else {
        toast.error('Não foi possível confirmar o pagamento.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar o pagamento no PayPal.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  const price = bundle?.prices[selectedModality] || 0;
  const installmentValue = price / formData.installments;
  const isConsultation = selectedModality === 'ead_live' || selectedModality === 'presential';
  const enrollmentFee = bundle?.installments?.enrollment_fee ?? 50;
  const installmentCount = bundle?.installments?.count ?? 20;
  const monthlyValue = bundle?.installments?.value ?? (price / installmentCount);
  const formatBRL = (v) => fmt(v);

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mr-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">Finalizar Matrícula</h1>
            <p className="text-gray-400">{bundle?.title} - {modalityLabels[selectedModality]}</p>
          </div>
        </div>

        {/* Seletor de país/moeda */}
        <div className="mb-8 bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
          <CurrencyToggle />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulário */}
          <div className="lg:col-span-2">
            <CheckoutForm
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              selectedModality={selectedModality}
              schedules={schedules}
              paymentProps={{
                formData,
                setFormData,
                price,
                isConsultation,
                installmentCount,
                monthlyValue,
                enrollmentFee,
                formatBRL,
                fmt,
                currency,
                paypalConfig,
                handlePaypalCreateOrder,
                handlePaypalApprove,
                processing,
              }}
            />
          </div>

          {/* Resumo */}
          <div>
            <OrderSummary
              bundle={bundle}
              selectedModality={selectedModality}
              onModalityChange={handleModalityChange}
              isConsultation={isConsultation}
              currency={currency}
              formatBRL={formatBRL}
              installmentCount={installmentCount}
              monthlyValue={monthlyValue}
              enrollmentFee={enrollmentFee}
              price={price}
            />
          </div>
        </div>

        {/* Botão Voltar ao Início */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <ArrowLeft className="mr-2" size={18} />
            Voltar ao Início
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;